/**
 * Performance Monitoring Utilities
 *
 * Provides performance profiling capabilities for Chrome DevTools.
 * Use to identify bottlenecks in IDB load, task transformation, dhtmlx rendering, and React reconciliation.
 */

/**
 * Performance Monitor Class
 *
 * Tracks performance marks and measures for profiling the Gantt Scheduler.
 * Results appear in Chrome DevTools Performance tab Timeline view.
 */
export class PerformanceMonitor {
  private marks = new Map<string, number>();
  private measures = new Map<string, number>();

  /**
   * Start measuring a section
   * Creates a performance mark visible in Chrome DevTools Timeline
   *
   * @param label - Name of the measurement
   */
  mark(label: string) {
    if (!performance.mark) return;

    const timestamp = performance.now();
    this.marks.set(label, timestamp);
    performance.mark(`${label}-start`);
    console.log(`[PERF] ${label} started`);
  }

  /**
   * End measurement and log results
   * Creates a performance measure between start and end marks
   *
   * @param label - Name of the measurement
   * @param startLabel - Optional custom start mark name
   * @returns Duration in milliseconds
   */
  measure(label: string, startLabel?: string) {
    if (!performance.measure) return;

    const start = startLabel || `${label}-start`;
    const end = `${label}-end`;

    performance.mark(end);
    try {
      performance.measure(label, start, end);
      const measure = performance.getEntriesByName(label)[0] as PerformanceMeasure;
      this.measures.set(label, measure.duration);

      console.log(`[PERF] ${label}: ${measure.duration.toFixed(2)}ms`);
      return measure.duration;
    } catch (e) {
      console.warn(`[PERF] Could not measure ${label}`, e);
    }
  }

  /**
   * Get all measurements as a report object
   *
   * @returns Object with measurement names as keys and durations as values
   */
  getReport() {
    return Object.fromEntries(this.measures);
  }

  /**
   * Clear all marks and measures
   * Useful for resetting profiling state between test runs
   */
  clear() {
    this.marks.clear();
    this.measures.clear();
    performance.clearMarks();
    performance.clearMeasures();
  }

  /**
   * Log a summary of all measurements
   */
  logSummary() {
    console.log('[PERF] Performance Summary:');
    console.table(this.getReport());
  }
}

/**
 * Global performance monitor instance
 * Use this throughout the app for consistent profiling
 */
export const perfMonitor = new PerformanceMonitor();
